import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { occupationCredentialEntityName } from '@juvare/credentialing/backend/config/classifiers/entities/defineOccupationCredentialClassifier';
import { OccupationCredentialColumns, OccupationCredentialData } from '@juvare/credentialing/backend/config/shared';
import { AuditFormatter } from '@juvare/platform-sdk-react/ui/features/audit/components/AuditDetailsDialog';
import { useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import last from 'lodash/last';
import startCase from 'lodash/startCase';
import upperFirst from 'lodash/upperFirst';
import React from 'react';

export const useAccountAuditFormatter = () => {
    const credentialStorage = useStorageClient<OccupationCredentialData, OccupationCredentialColumns>(occupationCredentialEntityName);
    const [credentialIdNameMap, setCredentialIdNameMap] = React.useState<Record<string, string>>({});

    const fetchCredentials = React.useCallback(async () => {
        const credentials = await credentialStorage.list({});

        const credentialsMap = credentials.rows.reduce<Record<string, string>>((map, credential) => {
            map[credential.id] = credential.columns.name;
            return map;
        }, {});

        setCredentialIdNameMap(credentialsMap);
    }, [credentialStorage]);

    React.useEffect(() => {
        fetchCredentials().catch(handleErrorSnackbar);
        // This useEffect is purposefully set with an empty dependency array to run only once when the component mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The key for the formatters object is a regular expression.
    // There is a default formatter defined which formats timestamp fields to human-readable format.
    // If you define two formatters, for example, module\..*\.id and module\..*, make sure the more specific formatter appears above the less specific formatter in the formatters list.
    // The following list is structured to ensure that more specific keys appear at the top, while more generic keys are placed towards the bottom.
    const accountEntityAuditFormatters: Record<string, AuditFormatter> = React.useMemo(
        () => ({
            'driversLicense.licenseNumber': () => ({ key: "Driver's License / ID Card Number" }),
            'driversLicense.licenseIssuingStateProvince': () => ({ key: 'License Issuing State / Province' }),
            'driversLicense.skillIds.*': () => ({ key: 'Licensed to Operate' }),
            'driversLicense.hazmat': () => ({ key: 'Certified to Transport Hazardous Materials' }),
            'occupations.*.groupId': () => ({ key: 'Occupation Group' }),
            'occupations.*.groupName': () => ({ key: 'Occupation Group' }),
            'occupations.*.occupationId': () => ({ key: 'Occupation' }),
            'occupations.*.occupationName': () => ({ key: 'Occupation' }),
            'occupations.*\\.ecl\\b': () => ({ key: 'ECL' }),
            '.*currentProfessionalStatusId': () => ({ key: 'Professional Status' }),
            'occupations.*.credentials.*.licenses': (key) => formatCredentialLicenses(key, credentialIdNameMap),
            'address.tribeId': () => ({ key: 'Address - Tribe' }),
            'skillsAndCertifications.healthcareSkillIds.*': () => ({ key: 'Healthcare Skills and Certifications' }),
            'skillsAndCertifications.otherSkillIds.*': () => ({ key: 'Other Relevant Skills and Certifications' }),
            '.*languageId': () => ({ key: 'Language' }),
            '.*spokenAbilityId': () => ({ key: 'Spoken Ability' }),
            '.*writtenAbilityId': () => ({ key: 'Written Ability' }),
            'trainings.*.courseId': () => ({ key: 'Trainings - Course Name' }),
            'trainings.*.institution': () => ({ key: 'Trainings - Institution' }),
            'trainings.*.courseDate': () => ({ key: 'Trainings - Course Date' }),
            'trainings.*.noExpirationDate': () => ({ key: 'Trainings - No Expiration Date' }),
            'trainings.*.expirationDate': () => ({ key: 'Trainings - Expiration Date' }),
            'trainings.*.files.*.name': () => ({ key: 'Trainings - File Name' }),
            'trainings.*.files.*.size': () => ({ key: 'Trainings - File Size' }),
            'trainings.*.files.*.entityKey': () => ({ key: 'Trainings - File Entity Key' }),
            'trainings.*.files.*.url': () => ({ key: 'Trainings - File URL' }),
            'trainings.*.verificationStatus': () => ({ key: 'Trainings - Verification Status' }),
            'trainings.*.verificationNotes': () => ({ key: 'Trainings - Verification Notes' }),
            'willingnessAndAvailability.organizationsIds.*': () => ({ key: 'Deployments - Organizations' }),
            'willingnessAndAvailability.validPassport': () => ({ key: 'Currently Hold a Valid US Passport' }),
            'willingnessAndAvailability.passportNumber': () => ({ key: 'Passport Number' }),
            'willingnessAndAvailability.workUnderFederalGovernment': () => ({ key: 'Work Under the Authority of the Federal Government?' }),
            'willingnessAndAvailability.deploymentTravelIds.*': () => ({ key: 'Willing to Travel for Deployment' }),
            'willingnessAndAvailability.deploymentDurationId.*': () => ({ key: 'Willing to Be Deployed for' }),
            'willingnessAndAvailability.covid19Vaccine': () => ({ key: 'COVID-19 Vaccine' }),
            'willingnessAndAvailability.medicalOrReligiousExemption': () => ({ key: 'Medical or Religious Exemption' }),
            'willingnessAndAvailability.haveOtherCommitments': () => ({ key: 'Other Commitments' }),
            'willingnessAndAvailability.priorEmergencyResponseCommitmentInformation': () => ({
                key: 'Prior Emergency Response Commitment Information',
            }),
            'willingnessAndAvailability.deployable': () => ({ key: 'Deployable' }),
            'activityPreferences.deploymentType.*': () => ({ key: 'Responder Activity' }),
            'priorDeployments.*.eventDate': () => ({ key: 'Initial Deployment Date' }),
            'priorDeployments.*.event': () => ({ key: 'Deployment Event' }),
            'priorDeployments.*.duration': () => ({ key: 'Days of Deployment' }),
            'priorDeployments.*.description': () => ({
                key: 'Description of Experience During Deployment',
            }),
            'contactPhoneNumbers.*.phoneNumber': () => ({ key: 'Phone Number' }),
            'contactPhoneNumbers.*.type.voice': () => ({ key: 'Phone Number Method - Voice' }),
            'contactPhoneNumbers.*.type.text': () => ({ key: 'Phone Number Method - Text' }),
            'contactPhoneNumbers.*.type.fax': () => ({ key: 'Phone Number Method - Fax' }),
            'contactEmails.*.email': () => ({ key: 'Additional Contact Email' }),
            'pagerNumbers.*.number': () => ({ key: 'Pager Number - Number' }),
            'pagerNumbers.*.carrier': () => ({ key: 'Pager Number - Carrier' }),
            'emergencyContacts.*.emergencyContact.phoneNumbers.*.phoneNumber': formatEmergencyContactPhoneNumber,
            'emergencyContacts.*.emergencyContact.phoneNumbers.*.type.*': formatEmergencyContactMethod,
            'emergencyContacts.*.emergencyContactEmail': () => ({ key: 'Emergency Contacts - Email' }),
            'emergencyContacts.*.emergencyContact.name': () => ({ key: 'Emergency Contacts - Name' }),
            'emergencyContacts.*.emergencyContact.relationship': () => ({ key: 'Emergency Contacts - Relationship' }),
            '.*licenseCheckRequestDate': (_key, oldValue, newValue) => formatDates('License Check Request Date', oldValue, newValue),
            'bouncedEmails.*': () => ({ key: 'Bounced Emails' }),
            'raceAndEthnicity.*': () => ({ key: 'Race and Ethnicity' }),
            'height.select1': () => ({ key: 'Height - Feet' }),
            'height.select': () => ({ key: 'Height - Inches' }),
            'weight.weight': () => ({ key: 'Weight' }),
            'driversLicense.sameNameAsPersonalLicence': () => ({
                key: 'Is the name on drivers license the same as the name you provided in your personal information?',
            }),
            firstNameLicence: () => ({ key: 'First Name License' }),
            lastNameLicense: () => ({ key: 'Last Name License' }),
            primaryBackgroundUpdatedAt: (_key, oldValue, newValue) =>
                formatDates('Primary Background Check Updated At', oldValue, newValue),
            primaryBackgroundCheckType: () => ({ key: 'Primary Background Check Type' }),
            recalculatePrimaryBackgroundCheckStatus: () => ({ key: 'Recalculate Primary Background Check Status' }),
            primaryBackgroundCheckStatus: () => ({ key: 'Primary Background Check Status' }),
            areYouOverTheAgeOf18: () => ({ key: 'Are You Over The Age of 18?' }),
            consentBackgroundCheck: () => ({ key: 'Consent to Background Check' }),
            consentInformation: () => ({ key: 'Consent to Correct Information Pledge' }),
            genderId: () => ({ key: 'Gender' }),
            esarVhpId: () => ({ key: 'ESAR VHP ID' }),
            captcha: () => ({ key: 'ReCAPTCHA' }),
            lastContactedAt: (_key, oldValue, newValue) => formatDates('Volunteer Last Contacted at', oldValue, newValue),
            stateOfWork: () => ({ key: 'State of Work' }),
            middleNameLicense: () => ({ key: 'Middle Name License' }),
            middleName: () => ({ key: 'Middle Name' }),
            dateOfBirth: () => ({ key: 'Date of Birth' }),
            ssn: () => ({ key: 'SSN' }),
            oigStatus: () => ({ key: 'OIG Status' }),
            trainUsername: () => ({ key: 'Train Username' }),
            systemRole: () => ({ key: 'System Role' }),
            systemStatus: () => ({ key: 'System Status' }),
            consent: () => ({ key: 'Consent to Terms of Service' }),
            orgRole: () => ({ key: 'Organization Role' }),
            orgStatus: () => ({ key: 'Organization Status' }),
            lastLogin: (_key, oldValue, newValue) => formatDates('Last Login', oldValue, newValue),
            'backgroundCheck.*.*': formatBackgroundCheck,
            'medicalHistory*': formatMedicalHistoryPath,
            'address.*': formatAddress,
            prefix: () => ({ key: 'Prefix' }),
            suffixLicense: () => ({ key: 'Suffix License' }),
            suffix: () => ({ key: 'Suffix' }),
            email: () => ({ key: 'Email' }),
            firstName: () => ({ key: 'First Name' }),
            lastName: () => ({ key: 'Last Name' }),
            maidenName: () => ({ key: 'Maiden Name (if applicable)' }),
            eyeColor: () => ({ key: 'Eye Color' }),
            hairColor: () => ({ key: 'Hair Color' }),
            updatedAt: (_key, oldValue, newValue) => formatDates('Updated at', oldValue, newValue),
        }),
        [credentialIdNameMap],
    );

    return accountEntityAuditFormatters;
};

export const formatDates = (key: string | undefined, oldValue: unknown, newValue: unknown) => ({
    ...(key && { key }),
    ...(typeof oldValue === 'number' && { oldValue: formatDateTime(new Date(oldValue)) }),
    ...(typeof newValue === 'number' && { newValue: formatDateTime(new Date(newValue)) }),
});

export function formatAddress(key: string) {
    const addressFieldKey = key.split('.').slice(1, 2)[0];
    const addressFieldName = formatToTitleCase(addressFieldKey);

    return {
        key: `Address - ${addressFieldName}`,
    };
}

export function formatEmergencyContactPhoneNumber(key: string) {
    const match = key.match(/phoneNumbers\.(\d+)\.phoneNumber/);
    const phoneSequence = match ? parseInt(match[1], 10) + 1 : undefined;

    return {
        key: `Emergency Contacts. Phone ${phoneSequence} - Number`,
    };
}

export function formatEmergencyContactMethod(key: string) {
    const match = key.match(/phoneNumbers\.(\d+)\.type/);
    const phoneNumberSequence = match ? parseInt(match[1], 10) + 1 : undefined;
    const method = last(key.split('.'));

    return {
        key: `Emergency Contacts. Phone ${phoneNumberSequence} Method - ${upperFirst(method)}`,
    };
}

export function formatBackgroundCheck(key: string, oldValue: unknown, newValue: unknown) {
    const formattedDates = formatDates(undefined, oldValue, newValue);
    const newKey = formatBackgroundCheckDataByType(key);

    return {
        ...(newKey && { key: newKey }),
        ...formattedDates,
    };
}

export function formatBackgroundCheckDataByType(key: string) {
    const parts = key.split('.');
    if (parts.length < 3) return undefined;
    const backgroundCheckType = upperFirst(parts[1]);
    const changeType = startCase(parts[2]);

    return `${backgroundCheckType} Background Check ${changeType}`;
}

export function formatCredentialLicenses(key: string, credentialIdNameMap: Record<string, string>) {
    const credentialId = extractCredentialId(key);
    const credentialName = credentialId ? credentialIdNameMap[credentialId] : undefined;

    if (!credentialName) {
        return {};
    }

    const fieldKey = key.split('.').slice(-1)[0];
    const licenseFieldName = formatToTitleCase(fieldKey);

    return {
        key: `${credentialName} - ${licenseFieldName}`,
    };
}

export function extractCredentialId(key: string) {
    const regex = /credentials\.([^.]*)\.licenses/;
    const match = key.match(regex);
    return match ? match[1] : undefined;
}

export function formatMedicalHistoryPath(key: string) {
    const modifiedKey = key
        .replace(/^medicalHistory/, '') // Remove medicalHistory
        .replace(/\./g, ' - ') // Replace dots with ' - '
        .replace(/([a-zA-Z])([0-9])/g, '$1 $2') // Separate letters followed by numbers
        .replace(/([0-9])([a-zA-Z])/g, '$1 $2') // Separate numbers followed by letters
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Separate lowercase letters followed by uppercase
        .trim()
        .split(' ')
        .map((word) => (word === '-' ? '-' : startCase(word))) // Ternary operator is used to prevent '-' removal by lodash
        .join(' ');

    return { key: modifiedKey };
}

export function formatToTitleCase(text: string): string {
    return text
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map((word) => startCase(word)) // Capitalize each word
        .join(' ');
}
